import React, { useCallback, useRef, useState } from "react";
import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Button,
	Stack,
	Text,
	Icon,
	Alert,
	AlertIcon,
	AlertDescription,
	Spinner,
	Box,
} from "@chakra-ui/react";
import { FiUploadCloud } from "react-icons/fi";
import { useAuth0 } from "@auth0/auth0-react";
import { ResumeData } from "./types";
import { logEvent } from "src/utlis/analytics";

const ALLOWED_TYPES = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ACCEPT_STRING = ".pdf,.doc,.docx";

interface CVUploadModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: (data: ResumeData) => void;
	onManualFill: () => void;
}

export const CVUploadModal: React.FC<CVUploadModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
	onManualFill,
}) => {
	const { getAccessTokenSilently } = useAuth0();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const validateFile = useCallback((file: File): string | null => {
		if (!ALLOWED_TYPES.includes(file.type)) {
			return "Please upload a PDF or Word document (.pdf, .doc, .docx).";
		}
		if (file.size > MAX_FILE_SIZE) {
			return "File is too large. Maximum size is 5MB.";
		}
		return null;
	}, []);

	const handleFileSelect = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (!file) return;

			const validationError = validateFile(file);
			if (validationError) {
				setError(validationError);
				setSelectedFile(null);
				return;
			}

			setError(null);
			setSelectedFile(file);
		},
		[validateFile]
	);

	const handleDrop = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			const file = event.dataTransfer.files?.[0];
			if (!file) return;

			const validationError = validateFile(file);
			if (validationError) {
				setError(validationError);
				setSelectedFile(null);
				return;
			}

			setError(null);
			setSelectedFile(file);
		},
		[validateFile]
	);

	const handleDragOver = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			event.preventDefault();
		},
		[]
	);

	const handleExtract = useCallback(async () => {
		if (!selectedFile) return;

		setIsUploading(true);
		setError(null);

		logEvent("cv_upload_attempted", {
			file_type: selectedFile.type,
			file_size_kb: Math.round(selectedFile.size / 1024),
			platform: "web",
		});

		try {
			const token = await getAccessTokenSilently();
			const formData = new FormData();
			formData.append("resume", selectedFile);

			const response = await fetch(
				`${process.env.REACT_APP_API_URL}/api/v1/pro-portfolio/parse-cv-for-prefill`,
				{
					method: "POST",
					headers: {
						authorization: token,
						authprovider: "auth0",
					},
					body: formData,
				}
			);

			const result = await response.json();

			if (!response.ok || !result.success) {
				const failureReason =
					result.error?.message ||
					"Failed to parse your CV. Please try again or use a different file.";
				setError(failureReason);
				logEvent("cv_extraction_failed", {
					failure_reason: failureReason,
					platform: "web",
				});
				return;
			}

			const data = result.data as ResumeData;

			const sections = [
				data.professionalSummary && "professional_summary",
				data.employmentHistoryItems?.length && "work_history",
				data.educationItems?.length && "education",
				data.certificationItems?.length && "certifications",
			].filter(Boolean);

			logEvent("cv_extraction_success", {
				extraction_confidence: data.confidence,
				platform: "web",
			});
			logEvent("cv_extraction_sections", {
				sections,
				platform: "web",
			});

			onSuccess(data);
		} catch {
			const failureReason =
				"Something went wrong while processing your CV. Please try again.";
			setError(failureReason);
			logEvent("cv_extraction_failed", {
				failure_reason: failureReason,
				platform: "web",
			});
		} finally {
			setIsUploading(false);
		}
	}, [selectedFile, getAccessTokenSilently, onSuccess]);

	const handleClose = useCallback(() => {
		if (isUploading) return;
		logEvent("cv_path_abandoned", {
			abandon_stage: "upload",
			platform: "web",
		});
		setSelectedFile(null);
		setError(null);
		onClose();
	}, [isUploading, onClose]);

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
			<ModalOverlay />
			<ModalContent rounded={0}>
				<ModalHeader color="primary.500">Upload your CV</ModalHeader>
				<ModalCloseButton />
				<ModalBody pb={6}>
					<Stack spacing={5}>
						<Box
							border="2px dashed"
							borderColor={selectedFile ? "green.400" : "#D7D7D7"}
							rounded={0}
							p={8}
							textAlign="center"
							cursor="pointer"
							onClick={() => fileInputRef.current?.click()}
							onDrop={handleDrop}
							onDragOver={handleDragOver}
							_hover={{ borderColor: "primary.500" }}
							transition="border-color 0.2s"
						>
							<input
								ref={fileInputRef}
								type="file"
								accept={ACCEPT_STRING}
								onChange={handleFileSelect}
								style={{ display: "none" }}
							/>
							<Stack alignItems="center" spacing={3}>
								<Icon
									as={FiUploadCloud}
									boxSize={10}
									color={selectedFile ? "green.500" : "gray.400"}
								/>
								{selectedFile ? (
									<>
										<Text fontWeight="medium" color="green.600">
											{selectedFile.name}
										</Text>
										<Text fontSize="sm" color="gray.500">
											Click to choose a different file
										</Text>
									</>
								) : (
									<>
										<Text fontWeight="medium">
											Drag & drop your CV here, or click to browse
										</Text>
										<Text fontSize="sm" color="gray.500">
											PDF or Word document, max 5MB
										</Text>
									</>
								)}
							</Stack>
						</Box>

						<Box bg="gray.50" p={4} rounded={0}>
							<Text fontWeight="medium" fontSize="sm" mb={2}>
								What makes a good CV for LaborPass?
							</Text>
							<Stack spacing={1}>
								<Text fontSize="sm" color="gray.600">
									- Clear employment history with dates
								</Text>
								<Text fontSize="sm" color="gray.600">
									- Education details with qualifications
								</Text>
								<Text fontSize="sm" color="gray.600">
									- Certifications and professional credentials
								</Text>
								<Text fontSize="sm" color="gray.600">
									- Project descriptions with outcomes
								</Text>
								<Text fontSize="sm" color="gray.600">
									- Professional summary or objective
								</Text>
							</Stack>
						</Box>

						{error && (
							<Stack spacing={2}>
								<Alert status="error" rounded={0}>
									<AlertIcon />
									<AlertDescription fontSize="sm">{error}</AlertDescription>
								</Alert>
								<Button
									variant="link"
									size="sm"
									color="gray.600"
									onClick={() => {
										logEvent("cv_path_abandoned", {
											abandon_stage: "upload",
											platform: "web",
										});
										onManualFill();
									}}
								>
									Fill in manually instead
								</Button>
							</Stack>
						)}

						<Button
							colorScheme="brand"
							rounded={0}
							size="lg"
							onClick={handleExtract}
							isDisabled={!selectedFile || isUploading}
							leftIcon={isUploading ? <Spinner size="sm" /> : undefined}
						>
							{isUploading ? "Reading your CV..." : "Extract my details"}
						</Button>
					</Stack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
